document.documentElement.classList.remove('no-js');

const q = (selector, scope = document) => scope.querySelector(selector);
const qa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'USD';
  return new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency',
    currency
  }).format((cents || 0) / 100);
}

function showToast(message) {
  const toast = q('[data-toast]');
  if (!toast) return;
  toast.textContent = message;
  toast.classList.add('is-visible');
  window.clearTimeout(showToast.timer);
  showToast.timer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
}

function initHeader() {
  const header = q('[data-site-header]');
  if (!header) return;
  const sync = () => header.classList.toggle('is-scrolled', window.scrollY > 60);
  sync();
  window.addEventListener('scroll', sync, { passive: true });

  const menu = q('[data-mobile-menu]');
  const overlay = q('[data-cart-overlay]');
  const setMenu = (open) => {
    if (!menu) return;
    menu.classList.toggle('is-open', open);
    menu.setAttribute('aria-hidden', String(!open));
    document.body.classList.toggle('menu-open', open);
    if (overlay) overlay.hidden = !open;
  };
  qa('[data-menu-open]').forEach((button) => button.addEventListener('click', () => setMenu(true)));
  qa('[data-menu-close]').forEach((button) => button.addEventListener('click', () => setMenu(false)));
}

function initTabs(scope = document) {
  qa('[data-tabs]', scope).forEach((tabs) => {
    const buttons = qa('[data-tab-button]', tabs);
    const panels = qa('[data-tab-panel]', tabs);
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const target = button.dataset.tabButton;
        buttons.forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', String(active));
        });
        panels.forEach((panel) => {
          panel.hidden = panel.dataset.tabPanel !== target;
        });
      });
    });
  });
}

function initSliders(scope = document) {
  qa('[data-card-slider]', scope).forEach((slider) => {
    const track = q('[data-card-track]', slider);
    if (!track) return;
    qa('[data-slide]', slider).forEach((button) => {
      button.addEventListener('click', () => {
        const direction = button.dataset.slide === 'next' ? 1 : -1;
        track.scrollBy({ left: track.clientWidth * 0.78 * direction, behavior: 'smooth' });
      });
    });
  });
}

const cartDrawer = () => q('[data-cart-drawer]');
const cartOverlay = () => q('[data-cart-overlay]');

function setCartDrawer(open) {
  const drawer = cartDrawer();
  const overlay = cartOverlay();
  if (!drawer) return;
  drawer.classList.toggle('is-open', open);
  drawer.setAttribute('aria-hidden', String(!open));
  document.body.classList.toggle('drawer-open', open);
  if (overlay) overlay.hidden = !open;
}

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value || '';
  return div.innerHTML;
}

async function refreshCart(open = false) {
  const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('Could not load cart');
  const cart = await response.json();
  qa('[data-cart-count]').forEach((count) => {
    count.textContent = cart.item_count;
    count.hidden = cart.item_count === 0;
  });

  const items = q('[data-cart-items]');
  const footer = q('[data-cart-footer]');
  const subtotal = q('[data-cart-subtotal]');
  if (items) {
    if (!cart.items.length) {
      items.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
    } else {
      items.innerHTML = cart.items.map((item) => `
        <article class="cart-line">
          <a href="${item.url}"><img src="${item.image || ''}" alt="" loading="lazy"></a>
          <div>
            <h3><a href="${item.url}">${escapeHtml(item.product_title)}</a></h3>
            ${item.variant_title ? `<p>${escapeHtml(item.variant_title)}</p>` : ''}
            <p>Qty: ${item.quantity}</p>
            <button class="cart-line__remove" type="button" data-remove-key="${item.key}">Remove</button>
          </div>
          <span class="cart-line__price">${formatMoney(item.final_line_price)}</span>
        </article>
      `).join('');
    }
  }
  if (footer) footer.hidden = cart.items.length === 0;
  if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
  if (open) setCartDrawer(true);
}

async function addToCart(form) {
  const button = q('button[type="submit"]', form);
  const original = button?.textContent;
  if (button) {
    button.disabled = true;
    button.textContent = 'Adding…';
  }
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Unable to add this item');
    }
    await refreshCart(true);
    showToast('Added to cart');
  } catch (error) {
    showToast(error.message);
  } finally {
    if (button) {
      button.disabled = false;
      button.textContent = original;
    }
  }
}

function initCart() {
  qa('[data-cart-open]').forEach((button) => {
    button.addEventListener('click', async () => {
      try {
        await refreshCart(true);
      } catch {
        setCartDrawer(true);
      }
    });
  });
  qa('[data-cart-close]').forEach((button) => button.addEventListener('click', () => setCartDrawer(false)));

  const overlay = cartOverlay();
  if (overlay) {
    overlay.addEventListener('click', () => {
      setCartDrawer(false);
      const menu = q('[data-mobile-menu]');
      menu?.classList.remove('is-open');
      document.body.classList.remove('menu-open');
      overlay.hidden = true;
    });
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('.js-product-form');
    if (!form) return;
    event.preventDefault();
    addToCart(form);
  });

  document.addEventListener('click', async (event) => {
    const remove = event.target.closest('[data-remove-key]');
    if (remove) {
      remove.disabled = true;
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: remove.dataset.removeKey, quantity: 0 })
      });
      await refreshCart();
    }
    const demo = event.target.closest('[data-demo-add]');
    if (demo) showToast('Connect this card to a Shopify product in the theme editor.');
  });
}

function initTheme(scope = document) {
  initTabs(scope);
  initSliders(scope);
}

document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initTheme();
  initCart();
});

document.addEventListener('shopify:section:load', (event) => initTheme(event.target));
